<template>
  <n-config-provider :theme-overrides="themeOverrides">
    <n-dialog-provider>
      <n-layout v-if="isAuthenticated" has-sider>
          <!-- Sidebar -->
          <n-layout-sider
            bordered
            :width="220"
            :collapsed-width="64"
            collapse-mode="width"
            :collapsed="collapsed"
            show-trigger
            @collapse="collapsed = true"
            @expand="collapsed = false"
          >
            <div class="logo">
              <span v-if="!collapsed">OnePlaceHQ Admin</span>
              <span v-else>OP</span>
            </div>
            <n-menu
              :collapsed="collapsed"
              :collapsed-width="64"
              :options="menuOptions"
              :value="currentRoute"
              @update:value="handleMenuClick"
            />
          </n-layout-sider>

          <!-- Main Content -->
          <n-layout>
            <n-layout-header bordered class="header">
              <div class="header-content">
                <span class="page-title">{{ pageTitle }}</span>
                <n-space>
                  <span>{{ user?.username }}</span>
                  <n-button size="small" @click="handleLogout">Logout</n-button>
                </n-space>
              </div>
            </n-layout-header>
            <n-layout-content class="main-content">
              <router-view />
            </n-layout-content>
          </n-layout>
        </n-layout>

        <!-- Login page (no layout) -->
        <router-view v-else />
    </n-dialog-provider>
  </n-config-provider>
</template>

<script setup>
import { ref, computed, h, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import {
  NConfigProvider,
  NDialogProvider,
  NLayout,
  NLayoutSider,
  NLayoutHeader,
  NLayoutContent,
  NMenu,
  NButton,
  NSpace,
  NIcon,
} from 'naive-ui'
import { AUTH_URL } from './config/api'

const router = useRouter()
const route = useRoute()

const collapsed = ref(false)
const user = ref(null)

const isAuthenticated = computed(() => {
  return !!localStorage.getItem('auth_token')
})

const currentRoute = computed(() => route.name)

const pageTitle = computed(() => {
  const titles = {
    dashboard: 'Dashboard',
    businesses: 'Businesses',
    'business-detail': 'Business Details',
    licenses: 'Licenses',
    payments: 'Payments',
    'music-library': 'Music Library',
    'whatsapp-admin': 'WhatsApp Admin',
  }
  return titles[route.name] || 'Admin'
})

const menuOptions = [
  {
    label: 'Dashboard',
    key: 'dashboard',
  },
  {
    label: 'Businesses',
    key: 'businesses',
  },
  {
    label: 'Licenses',
    key: 'licenses',
  },
  {
    label: 'Payments',
    key: 'payments',
  },
  {
    label: 'Music Library',
    key: 'music-library',
  },
  {
    label: 'WhatsApp',
    key: 'whatsapp-admin',
  },
  {
    label: 'HSN Rates',
    key: 'hsn-rates',
  },
]

const themeOverrides = {
  common: {
    primaryColor: '#18181b',
    primaryColorHover: '#27272a',
    primaryColorPressed: '#09090b',
  },
}

const handleMenuClick = (key) => {
  router.push({ name: key })
}

const handleLogout = () => {
  localStorage.removeItem('auth_token')
  user.value = null
  router.push('/login')
}

onMounted(async () => {
  const token = localStorage.getItem('auth_token')
  if (token) {
    try {
      const response = await fetch(`${AUTH_URL}/me`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (response.ok) {
        const data = await response.json()
        user.value = data.data?.user
      } else {
        localStorage.removeItem('auth_token')
        router.push('/login')
      }
    } catch (error) {
      console.error('Auth check failed:', error)
    }
  }
})
</script>

<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html, body, #app {
  height: 100%;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
}

.n-layout {
  height: 100%;
}

.logo {
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 600;
  color: #18181b;
  border-bottom: 1px solid #e5e5e5;
}

.header {
  height: 64px;
  padding: 0 24px;
  display: flex;
  align-items: center;
}

.header-content {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.page-title {
  font-size: 18px;
  font-weight: 600;
}

.main-content {
  padding: 24px;
  background: #f5f5f5;
  min-height: calc(100vh - 64px);
}
</style>
